import { collection, addDoc, query, where, getDocs, doc, updateDoc, Timestamp, getDoc, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { Teacher } from '../types';
import { getQrupConfig, normalizeQrup } from '../constants/fennConfig';
import {
  FOREIGN_LANGUAGES,
  BuraxilisBlankContext,
  Grade11BlankScheme,
  getBuraxilisSubjectsForGrade,
} from '../constants/buraxilisConfig';

/** Sual nömrəsindən fənn tapmaq (grade 11 blank scheme aware) */
export const getFennFromQuestionNumber = (
  questionNumber: string,
  grade: number,
  ctx?: BuraxilisBlankContext | Grade11BlankScheme | null
): string | null => {
  for (const subjectGroup of getBuraxilisSubjectsForGrade(grade, ctx)) {
    if (!subjectGroup.questions.includes(questionNumber)) continue;
    return FOREIGN_LANGUAGES.includes(subjectGroup.subject)
      ? 'Xarici dil'
      : subjectGroup.subject;
  }
  return null;
};

// Blok sual nömrəsindən fənn tapmaq üçün helper funksiya
// Qrup config-dən istifadə edərək sual nömrəsinə görə fənn tapır
export const getFennFromQuestionNumberForBlok = (questionNumber: string, qrup: string, subGroup?: string): string | null => {
  // Extract the numeric part from questionNumber (e.g., "Q28" -> 28)
  const match = questionNumber.match(/^Q(\d+)$/);
  if (!match) return null;

  const qNum = parseInt(match[1], 10);

  // Find the qrup config (1/2/3/4 → I/II/III/IV)
  const qrupConfig = getQrupConfig(normalizeQrup(qrup) || qrup);
  if (!qrupConfig) return null;

  // Search through fenn configurations
  for (const fennConfig of qrupConfig.fennler) {
    // Check if question number is within this fenn's range
    if (qNum >= fennConfig.start && qNum <= fennConfig.end) {
      // If subGroup is specified, check if it matches
      if (subGroup && fennConfig.subGroup) {
        // Normalize subGroup for comparison (handle RI/Rİ, etc.)
        const normalizeSubGroup = (sg: string): string => sg.toUpperCase().replace('I', 'İ');
        if (normalizeSubGroup(subGroup) === normalizeSubGroup(fennConfig.subGroup)) {
          return fennConfig.fenn;
        }
      } else if (!fennConfig.subGroup) {
        // No subGroup restriction
        return fennConfig.fenn;
      }
    }
  }

  return null;
};

// Xarici dil fənn adını normalize et (İngilis dili, Rus dili, etc.)
const normalizeForeignLanguage = (xdil: string | undefined, subject: string): string => {
  if (subject !== 'Xarici dil') return subject;
  
  if (!xdil) return 'İngilis dili'; // Default
  
  const normalized = xdil.trim();
  if (normalized === 'İngilis' || normalized === 'Rus' || normalized === 'Alman' || normalized === 'Fransız') {
    return `${normalized} dili`;
  }
  return normalized;
};

// Apellyasiya yaradır
export const createApellyasiya = async (
  studentId: string,
  studentDocumentId: string,
  questionNumber: string,
  apellyasiyaSəbəbi: string,
  teacherId: string,
  teacherName: string,
  collectionName: string,
  grade: number,
  previousGrading: {
    hocaId: string;
    hoca: string;
    score: string;
    timestamp: any;
    note?: string;
  },
  xdil?: string, // Xarici dil üçün
  blankCtx?: BuraxilisBlankContext | Grade11BlankScheme | null
): Promise<{ success: boolean; message: string; apellyasiyaId?: string }> => {
  try {
    console.log('📝 apellyasiyaService: Apellyasiya yaradılır...', {
      studentId,
      questionNumber,
      teacherId,
      collectionName
    });

    // Sual nömrəsindən fənn tap (grade 11 blank scheme aware)
    const subject = getFennFromQuestionNumber(questionNumber, grade, blankCtx);
    if (!subject) {
      return { success: false, message: 'Sual nömrəsi üçün fənn tapılmadı' };
    }

    // Xarici dil üçün normalize et
    const fenn = normalizeForeignLanguage(xdil, subject);

    const apellyasiyaData = {
      type: 'apellyasiya',
      hocaId: teacherId,
      hocaName: teacherName,
      studentId: studentId,
      studentDocumentId: studentDocumentId,
      questionNumber: questionNumber,
      collectionName: collectionName,
      examType: 'buraxilis',
      fenn: fenn, // Fənn adı
      
      // Apellyasiya məlumatları
      apellyasiya_tarixi: Timestamp.now(),
      apellyasiya_səbəbi: apellyasiyaSəbəbi,
      apellyasiya_status: 'pending',
      
      // Əvvəlki yoxlama məlumatları
      əvvəlki_yoxlama: {
        hocaId: previousGrading.hocaId,
        hoca: previousGrading.hoca,
        score: previousGrading.score,
        timestamp: previousGrading.timestamp,
        ...(previousGrading.note && { note: previousGrading.note })
      },
      
      status: 'active',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'yoxla-s'), apellyasiyaData);
    console.log('✅ apellyasiyaService: Apellyasiya yaradıldı:', docRef.id);

    return {
      success: true,
      message: 'Apellyasiya uğurla yaradıldı',
      apellyasiyaId: docRef.id
    };
  } catch (error) {
    console.error('❌ apellyasiyaService: Apellyasiya yaradılarkən xəta:', error);
    throw error;
  }
};

// Blok üçün apellyasiya yaradır
export const createApellyasiyaForBlok = async (
  studentId: string,
  studentDocumentId: string,
  questionNumber: string,
  apellyasiyaSəbəbi: string,
  teacherId: string,
  teacherName: string,
  collectionName: string,
  qrup: string,
  subGroup: string | undefined,
  previousGrading: {
    hocaId: string;
    hoca: string;
    score: string;
    timestamp: any;
    note?: string;
  }
): Promise<{ success: boolean; message: string; apellyasiyaId?: string }> => {
  try {
    console.log('📝 apellyasiyaService: Blok apellyasiya yaradılır...', {
      studentId,
      questionNumber,
      teacherId,
      collectionName,
      qrup,
      subGroup
    });

    // Sual nömrəsindən fənn tap (Blok üçün)
    const fenn = getFennFromQuestionNumberForBlok(questionNumber, qrup, subGroup);
    if (!fenn) {
      return { success: false, message: `Sual nömrəsi üçün fənn tapılmadı (qrup: ${qrup})` };
    }

    const apellyasiyaData = {
      type: 'apellyasiya',
      hocaId: teacherId,
      hocaName: teacherName,
      studentId: studentId,
      studentDocumentId: studentDocumentId,
      questionNumber: questionNumber,
      collectionName: collectionName,
      examType: 'Blok',
      fenn: fenn,
      qrup: qrup,
      subGroup: subGroup,

      // Apellyasiya məlumatları
      apellyasiya_tarixi: Timestamp.now(),
      apellyasiya_səbəbi: apellyasiyaSəbəbi,
      apellyasiya_status: 'pending',

      // Əvvəlki yoxlama məlumatları
      əvvəlki_yoxlama: {
        hocaId: previousGrading.hocaId,
        hoca: previousGrading.hoca,
        score: previousGrading.score,
        timestamp: previousGrading.timestamp,
        ...(previousGrading.note && { note: previousGrading.note })
      },

      status: 'active',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'yoxla-s'), apellyasiyaData);
    console.log('✅ apellyasiyaService: Blok apellyasiya yaradıldı:', docRef.id);

    return {
      success: true,
      message: 'Apellyasiya uğurla yaradıldı',
      apellyasiyaId: docRef.id
    };
  } catch (error) {
    console.error('❌ apellyasiyaService: Blok apellyasiya yaradılarkən xəta:', error);
    throw error;
  }
};

// Müəllim üçün apellyasiyaları al
export const getApellyasiyasByTeacher = async (teacherId: string): Promise<any[]> => {
  try {
    console.log('🔍 apellyasiyaService: Müəllim üçün apellyasiyalar alınır:', teacherId);
    
    const q = query(
      collection(db, 'yoxla-s'),
      where('type', '==', 'apellyasiya'),
      where('hocaId', '==', teacherId),
      where('apellyasiya_status', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    const apellyasiyas: any[] = [];
    
    querySnapshot.forEach((doc) => {
      apellyasiyas.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    console.log('✅ apellyasiyaService: Müəllim üçün apellyasiyalar:', apellyasiyas.length);
    return apellyasiyas;
  } catch (error) {
    console.error('❌ apellyasiyaService: Apellyasiyalar alınarkən xəta:', error);
    throw error;
  }
};

// Get all apellyasiyas for a specific exam collection
export const getApellyasiyasByCollection = async (
  collectionName: string,
  teacherId?: string
): Promise<any[]> => {
  try {
    console.log('🔍 apellyasiyaService: Collection üçün apellyasiyalar alınır:', collectionName);
    
    const conditions: any[] = [
      where('type', '==', 'apellyasiya'),
      where('collectionName', '==', collectionName)
    ];
    
    // If teacherId provided, filter by teacher
    if (teacherId) {
      conditions.push(where('hocaId', '==', teacherId));
    }
    
    const q = query(
      collection(db, 'yoxla-s'),
      ...conditions
    );
    
    const querySnapshot = await getDocs(q);
    const apellyasiyas: any[] = [];
    
    querySnapshot.forEach((doc) => {
      apellyasiyas.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    console.log('✅ apellyasiyaService: Collection üçün apellyasiyalar:', apellyasiyas.length);
    return apellyasiyas;
  } catch (error) {
    console.error('❌ apellyasiyaService: Apellyasiyalar alınarkən xəta:', error);
    throw error;
  }
};

// Tələbə üçün apellyasiyaları al
export const getApellyasiyasByStudent = async (studentId: string, collectionName: string): Promise<any[]> => {
  try {
    console.log('🔍 apellyasiyaService: Tələbə üçün apellyasiyalar alınır:', studentId);
    
    const q = query(
      collection(db, 'yoxla-s'),
      where('type', '==', 'apellyasiya'),
      where('studentId', '==', studentId),
      where('collectionName', '==', collectionName)
    );
    
    const querySnapshot = await getDocs(q);
    const apellyasiyas: any[] = [];
    
    querySnapshot.forEach((doc) => {
      apellyasiyas.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    console.log('✅ apellyasiyaService: Tələbə üçün apellyasiyalar:', apellyasiyas.length);
    return apellyasiyas;
  } catch (error) {
    console.error('❌ apellyasiyaService: Apellyasiyalar alınarkən xəta:', error);
    throw error;
  }
};

// Eyni imtahanın növbəti gözləyən apellyasiyasını al
export const getNextPendingApellyasiya = async (
  collectionName: string,
  teacherId: string,
  excludeApellyasiyaId?: string
): Promise<any | null> => {
  try {
    console.log('🔍 apellyasiyaService: Növbəti apellyasiya axtarılır...', {
      collectionName,
      teacherId,
      excludeApellyasiyaId
    });
    
    const conditions: any[] = [
      where('type', '==', 'apellyasiya'),
      where('collectionName', '==', collectionName),
      where('hocaId', '==', teacherId),
      where('apellyasiya_status', '==', 'pending')
    ];
    
    const q = query(
      collection(db, 'yoxla-s'),
      ...conditions,
      orderBy('apellyasiya_tarixi', 'asc'),
      limit(10) // İlk 10-u al, sonra exclude edəcəyik
    );
    
    const querySnapshot = await getDocs(q);
    
    // Exclude ediləcək apellyasiyanı çıxar və ilkini qaytar
    for (const docSnapshot of querySnapshot.docs) {
      if (excludeApellyasiyaId && docSnapshot.id === excludeApellyasiyaId) {
        continue;
      }
      
      const apellyasiyaData = {
        id: docSnapshot.id,
        ...docSnapshot.data()
      };
      
      console.log('✅ apellyasiyaService: Növbəti apellyasiya tapıldı:', apellyasiyaData.id);
      return apellyasiyaData;
    }
    
    console.log('ℹ️ apellyasiyaService: Növbəti apellyasiya tapılmadı');
    return null;
  } catch (error) {
    console.error('❌ apellyasiyaService: Növbəti apellyasiya axtarılarkən xəta:', error);
    // Eğer orderBy hatası varsa (index yoksa), basit query dene
    try {
      const conditions: any[] = [
        where('type', '==', 'apellyasiya'),
        where('collectionName', '==', collectionName),
        where('hocaId', '==', teacherId),
        where('apellyasiya_status', '==', 'pending')
      ];
      
      const q = query(
        collection(db, 'yoxla-s'),
        ...conditions
      );
      
      const querySnapshot = await getDocs(q);
      
      // Exclude ediləcək apellyasiyanı çıxar və ilkini qaytar
      for (const docSnapshot of querySnapshot.docs) {
        if (excludeApellyasiyaId && docSnapshot.id === excludeApellyasiyaId) {
          continue;
        }
        
        const apellyasiyaData = {
          id: docSnapshot.id,
          ...docSnapshot.data()
        };
        
        console.log('✅ apellyasiyaService: Növbəti apellyasiya tapıldı (fallback):', apellyasiyaData.id);
        return apellyasiyaData;
      }
      
      return null;
    } catch (fallbackError) {
      console.error('❌ apellyasiyaService: Fallback query də xəta:', fallbackError);
      throw fallbackError;
    }
  }
};

// Apellyasiyanı təsdiqlə (müəllim tərəfindən)
export const confirmApellyasiya = async (
  apellyasiyaId: string,
  studentDocumentId: string,
  questionNumber: string,
  collectionName: string,
  apellyasiyaNote: string,
  newScore: string,
  previousScore: string,
  teacherId: string,
  teacherName: string
): Promise<void> => {
  try {
    console.log('✅ apellyasiyaService: Apellyasiya təsdiqlənir...', {
      apellyasiyaId,
      questionNumber,
      newScore,
      previousScore
    });

    // Q##_c field-ə apellyasiya məlumatlarını yaz
    const studentDocRef = doc(db, collectionName, studentDocumentId);
    const studentDoc = await getDoc(studentDocRef);
    
    if (!studentDoc.exists()) {
      throw new Error('Tələbə sənədi tapılmadı');
    }

    const currentGradingData = studentDoc.data()[`${questionNumber}_c`] || {};
    
    // Gerçek əvvəlki balı öğrenci dokümanından al (parametrə güvən etmə)
    // Q26 üçün: criteria varsa toplam puanı hesabla, yoxsa score-u istifadə et
    let actualPreviousScore = '0';
    if (currentGradingData && typeof currentGradingData === 'object') {
      if (currentGradingData.criteria) {
        // Q26 formatı: criteria-dan toplam puanı hesabla
        const { content = 0, sequence = 0, grammar = 0, vocabulary = 0, penalty = 0 } = currentGradingData.criteria;
        const total = content + sequence + grammar + vocabulary - penalty;
        actualPreviousScore = total.toString();
      } else if (currentGradingData.score) {
        // Normal format: score-u istifadə et
        actualPreviousScore = currentGradingData.score.toString();
      }
    }
    
    const scoreChanged = actualPreviousScore !== newScore;
    
    // Yeni bal varsa, score-u yenilə
    const updatedGradingData: any = {
      ...currentGradingData,
      apellyasiya_note: apellyasiyaNote,
      apellyasiya_result: true,
      previous_score: actualPreviousScore, // Gerçek əvvəlki bal
      new_score: newScore,
      apellyasiya_status: true,
      apellyasiya_onaylanma_tarihi: Timestamp.now(),
      apellyasiya_timestamp: Timestamp.now()
    };

    // Əgər müəllim fərqlidirsə, apellyasiya_hocaId və apellyasiya_hoca əlavə et
    if (currentGradingData.hocaId !== teacherId) {
      updatedGradingData.apellyasiya_hocaId = teacherId;
      updatedGradingData.apellyasiya_hoca = teacherName;
    }

    // Bal dəyişdiyi halda, score-u yenilə
    if (scoreChanged) {
      updatedGradingData.score = newScore;
    }

    // Sadece öğrenci document'indeki Q##_c field'ına yaz (yoxla-s document'ini güncelleme - permissions hatasına neden oluyor)
    await updateDoc(studentDocRef, {
      [`${questionNumber}_c`]: updatedGradingData
    });

    console.log('✅ apellyasiyaService: Apellyasiya təsdiqləndi - Q##_c field-ə yazıldı');
  } catch (error) {
    console.error('❌ apellyasiyaService: Apellyasiya təsdiqlənərkən xəta:', error);
    throw error;
  }
};

// Fənn üçün müəllimləri al (apellyasiya təyin etmə üçün)
export const getTeachersByFenn = async (fenn: string): Promise<Teacher[]> => {
  try {
    console.log('🔍 apellyasiyaService: Fənn üçün müəllimlər alınır:', fenn);
    
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('status', '==', 'teacher'));
    const querySnapshot = await getDocs(q);
    
    const teachers: Teacher[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const teacherFenns = (data.fenn || '').split(',').map((f: string) => f.trim());
      
      // Fənn uyğunluğunu yoxla
      // Xarici dil üçün xüsusi yoxlama
      if (fenn === 'Xarici dil' || fenn.includes('dil')) {
        // Xarici dil fənnləri: İngilis dili, Rus dili, Alman dili, Fransız dili
        const foreignLanguages = ['İngilis dili', 'Rus dili', 'Alman dili', 'Fransız dili'];
        const hasForeignLanguage = teacherFenns.some((tf: string) => 
          foreignLanguages.some(fl => tf.includes(fl) || fl.includes(tf))
        );
        if (hasForeignLanguage) {
          teachers.push({
            id: doc.id,
            name: data.Name,
            fenn: data.fenn,
            status: data.status,
            email: data.email || '',
            isActive: data.isActive !== false
          });
        }
      } else {
        // Digər fənnlər üçün dəqiq uyğunluq
        const hasFenn = teacherFenns.some((tf: string) => 
          tf === fenn || tf.includes(fenn) || fenn.includes(tf)
        );
        if (hasFenn) {
          teachers.push({
            id: doc.id,
            name: data.Name,
            fenn: data.fenn,
            status: data.status,
            email: data.email || '',
            isActive: data.isActive !== false
          });
        }
      }
    });
    
    console.log('✅ apellyasiyaService: Fənn üçün müəllimlər:', teachers.length);
    return teachers;
  } catch (error) {
    console.error('❌ apellyasiyaService: Müəllimlər alınarkən xəta:', error);
    throw error;
  }
};


